import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Switch } from "@/components/ui/switch";
import { UserPlus, CurrencyDollar, Users, Crown, TrendUp, Lightning, ChatCircleText } from "@phosphor-icons/react";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return isNaN(d) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const timeAgo = (s) => {
  if (!s) return "";
  const diff = (Date.now() - new Date(s).getTime()) / 1000;
  if (isNaN(diff)) return "";
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

function Stat({ icon: Icon, label, value, sub, tone = "text-zinc-300", testId }) {
  return (
    <div data-testid={testId} className="bg-zinc-900 border border-white/10 rounded-lg p-4">
      <div className="flex items-center gap-2 text-zinc-500 text-xs font-medium uppercase tracking-wider">
        <Icon size={15} className={tone} /> {label}
      </div>
      <div className="mt-2 font-heading text-2xl font-bold tracking-tight">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboardingOn, setOnboardingOn] = useState(null);
  const [savingOnboarding, setSavingOnboarding] = useState(false);
  const [webrtcOn, setWebrtcOn] = useState(null);
  const [savingWebrtc, setSavingWebrtc] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/admin/stats")
      .then((res) => { if (alive) setStats(res.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    api.get("/admin/settings/onboarding")
      .then((res) => { if (alive) setOnboardingOn(res.data.enabled); })
      .catch(() => {});
    api.get("/admin/settings/webrtc")
      .then((res) => { if (alive) setWebrtcOn(res.data.enabled); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const toggleOnboarding = async (val) => {
    setOnboardingOn(val);
    setSavingOnboarding(true);
    try {
      await api.post("/admin/settings/onboarding", { enabled: val });
    } catch {
      setOnboardingOn(!val);
    } finally {
      setSavingOnboarding(false);
    }
  };

  const toggleWebrtc = async (val) => {
    setWebrtcOn(val);
    setSavingWebrtc(true);
    try {
      await api.post("/admin/settings/webrtc", { enabled: val });
    } catch {
      setWebrtcOn(!val);
    } finally {
      setSavingWebrtc(false);
    }
  };

  if (loading) return <div className="text-zinc-500 font-mono text-sm animate-pulse py-8">Loading overview…</div>;
  if (!stats) return <div className="text-zinc-500 text-sm py-8">Could not load stats.</div>;

  const { users, revenue, subscriptions, recent_signups, recent_sales } = stats;

  return (
    <div data-testid="admin-overview" className="space-y-6">
      <div data-testid="onboarding-toggle-card" className="bg-zinc-900 border border-white/10 rounded-lg p-4 flex items-center justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <ChatCircleText size={20} className="text-blue-400 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-sm font-semibold">Ask "How did you hear about us?"</div>
            <div className="text-xs text-zinc-500 mt-0.5">When on, brand-new signups are asked once after their first login. Existing users are never interrupted.</div>
          </div>
        </div>
        <Switch
          data-testid="onboarding-enabled-switch"
          checked={!!onboardingOn}
          disabled={onboardingOn === null || savingOnboarding}
          onCheckedChange={toggleOnboarding}
        />
      </div>

      <div data-testid="webrtc-toggle-card" className="bg-zinc-900 border border-white/10 rounded-lg p-4 flex items-center justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <Lightning size={20} className="text-amber-400 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-sm font-semibold">WebRTC direct streaming <span className="text-[10px] rounded-full bg-amber-500/15 text-amber-300 px-1.5 py-0.5 ml-1">beta · scaffolding</span></div>
            <div className="text-xs text-zinc-500 mt-0.5">Phase 0 placeholder. When on, the parallel WebRTC path becomes reachable for testing. It does not stream yet, and the standard secure relay stays the default for everyone.</div>
          </div>
        </div>
        <Switch
          data-testid="webrtc-enabled-switch"
          checked={!!webrtcOn}
          disabled={webrtcOn === null || savingWebrtc}
          onCheckedChange={toggleWebrtc}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat testId="stat-new-7d" icon={UserPlus} tone="text-blue-400" label="New (7d)" value={users.new_7d} sub={`${users.new_30d} in 30d`} />
        <Stat testId="stat-total-users" icon={Users} label="Total users" value={users.total} />
        <Stat testId="stat-revenue-7d" icon={CurrencyDollar} tone="text-emerald-400" label="Revenue (7d)" value={money(revenue.d7)} sub={`${revenue.sales_7d} sale${revenue.sales_7d === 1 ? "" : "s"}`} />
        <Stat testId="stat-revenue-30d" icon={TrendUp} tone="text-emerald-400" label="Revenue (30d)" value={money(revenue.d30)} sub={`${revenue.sales_30d} sale${revenue.sales_30d === 1 ? "" : "s"}`} />
        <Stat testId="stat-active-paid" icon={Lightning} tone="text-amber-400" label="Paid plans" value={subscriptions.active_paid} />
        <Stat testId="stat-lifetime" icon={Crown} tone="text-amber-400" label="Lifetime" value={subscriptions.lifetime} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-zinc-900 border border-white/10 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 text-sm font-semibold">
            <UserPlus size={16} className="text-blue-400" /> Recent signups
          </div>
          <div data-testid="recent-signups" className="divide-y divide-white/5">
            {recent_signups.length === 0 ? (
              <div className="px-4 py-6 text-zinc-500 text-sm">No signups yet.</div>
            ) : recent_signups.map((u, i) => (
              <div key={i} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm truncate">{u.name || u.email}</div>
                  <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                </div>
                <div className="text-xs text-zinc-500 shrink-0">{fmtDate(u.created_at)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-zinc-900 border border-white/10 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 text-sm font-semibold">
            <CurrencyDollar size={16} className="text-emerald-400" /> Recent sales
          </div>
          <div data-testid="recent-sales" className="divide-y divide-white/5">
            {recent_sales.length === 0 ? (
              <div className="px-4 py-6 text-zinc-500 text-sm">No sales yet.</div>
            ) : recent_sales.map((s, i) => (
              <div key={i} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm truncate flex items-center gap-1.5">
                    {s.plan_name}
                    {s.type === "lifetime" && <span className="text-[10px] rounded-full bg-amber-500/15 text-amber-300 px-1.5 py-0.5">lifetime</span>}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">{s.email} · {timeAgo(s.at)}</div>
                </div>
                <div className="text-sm font-semibold text-emerald-400 shrink-0">{money(s.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
